import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Spinner from 'components/shared/Spinner';

import type { ImageNodeStyleProp } from 'types/node_types';

export type ImageNodeProps = {
  uid: string;
  imageSrc: string;
  cropLoading: boolean;
  styles: ImageNodeStyleProp;
  imageStyle: React.CSSProperties;
  imgAlt: string;
  isActive: boolean;
  isMultiNodeProduct: boolean;
  onLoad: React.ReactEventHandler<HTMLImageElement>;
  onClear: React.MouseEventHandler;
  onClick?: React.MouseEventHandler;
  onDragEnter?: React.DragEventHandler;
  onDragLeave?: React.DragEventHandler;
  onDrop?: React.DragEventHandler;
  onPan?: (x: number, y: number) => void;
};

function ImageNode({
  uid,
  imageSrc,
  cropLoading,
  styles,
  imageStyle,
  imgAlt,
  isActive,
  isMultiNodeProduct,
  onLoad,
  onClear,
  onClick,
  onDragEnter,
  onDragLeave,
  onDrop,
  onPan,
}: ImageNodeProps) {
  // Handle panning an image started with a mouse event
  function handleMousePan() {
    if (!onPan) return;

    function trackmove({ movementX, movementY }: MouseEvent) {
      onPan && onPan(movementX, movementY);
    }

    document.addEventListener('mousemove', trackmove);
    document.addEventListener(
      'mouseup',
      () => {
        document.removeEventListener('mousemove', trackmove);
      },
      { once: true }
    );
  }

  // Handle panning an image started by a touch event
  // touchmove event has slight differences to a mousemove event, so has to be handled differently
  function handleTouchPan(event: React.TouchEvent<HTMLImageElement>) {
    if (!onPan) return;

    event.preventDefault();
    let previousTouch: React.Touch | undefined;

    function trackmove(e: TouchEvent) {
      e.preventDefault();
      const touch = e.touches[0];

      let movementX;
      let movementY;

      if (previousTouch) {
        movementX = touch.pageX - previousTouch.pageX;
        movementY = touch.pageY - previousTouch.pageY;
        previousTouch = touch;
      } else {
        previousTouch = touch;
        return;
      }

      onPan && onPan(movementX, movementY);
    }

    document.addEventListener('touchmove', trackmove, { passive: false });
    document.addEventListener(
      'touchend',
      () => {
        previousTouch = undefined;
        document.removeEventListener('touchmove', trackmove);
      },
      { once: true }
    );
  }

  return (
    // TODO: Any better way to handle this?
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`image_node ${imageSrc ? 'has_image' : ''}`}
      // testid & uid are required for testing
      data-testid="image-node"
      data-uid={uid}
      style={styles.node}
      onClick={onClick}
      // Dropping requires handling both dragenter/dragover events and calling preventDefault
      // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <button className="trigger_clear_image" onClick={onClear}>
        <FontAwesomeIcon icon="times" color="white" fixedWidth />
        <span className="screen-reader-text">Remove image from node</span>
      </button>
      <div
        className={`img_wrap ${isActive && isMultiNodeProduct ? ' active' : ''}`}
        style={styles.imgWrap}
      >
        <div className="cropFrame" style={styles.cropFrame}>
          {imageSrc !== '' && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <img
              onLoad={onLoad}
              onMouseDown={handleMousePan}
              onTouchStart={handleTouchPan}
              src={imageSrc}
              style={imageStyle}
              alt={`Your upload, titled ${imgAlt}`}
              className="cropImage"
              draggable={false} // prevents ghost image while panning
              onDragStart={(e) => e.preventDefault()} // prevents highlighting while panning
            />
          )}
        </div>
      </div>
      {cropLoading && (
        <Spinner
          color="#fff"
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#252525',
          }}
        />
      )}
    </div>
  );
}

export default ImageNode;
